import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { ICON_CATEGORIES, ICON_REGISTRY, searchIcons, getIcon } from "./iconRegistry";
import { elakayAutoText } from "../dataElakay";


// ---------------------------------------------------------------------------
// iconKit/IconPicker — sélecteur d'icônes partagé : recherche, catégories,
// grille avec aperçu, sélection. Construit générique (props seulement,
// aucune dépendance à un éditeur précis) pour être réutilisé tel quel par
// pageBuilder, HowWeDoItEditor et emailMarketingEditor.
//
// value: iconId actuellement sélectionné (mis en évidence dans la grille).
// onSelect(iconId): appelé au clic sur une icône.
// ---------------------------------------------------------------------------
export default function IconPicker({ value, onSelect }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");

  const results = useMemo(() => {
    const base = query.trim() ? searchIcons(query) : ICON_REGISTRY;
    if (!category) return base;
    return base.filter((icon) => icon.category === category);
  }, [query, category]);

  const selected = getIcon(value);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <div style={{ position: "relative" }}>
        <Search size={14} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#9b93b8" }} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={elakayAutoText("h_7db13f556d0b3e", "Rechercher une icône…")}
          aria-label={elakayAutoText("h_c37c5e007af9cd", "Rechercher une icône")}
          style={{
            width: "100%", padding: "8px 10px 8px 30px", fontSize: "13px",
            border: "1px solid #e4e0f0", borderRadius: "8px", outline: "none",
            color: "#2a2547", background: "#faf9fd",
          }}
        />
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
        <button
          type="button"
          onClick={() => setCategory("")}
          style={categoryPillStyle(category === "")}
        >{elakayAutoText("h_c5f641e4d1cc19", "Toutes")}</button>
        {ICON_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={() => setCategory(cat.id)}
            style={categoryPillStyle(category === cat.id)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div
        role="listbox"
        aria-label={elakayAutoText("h_d29ad187a9f10d", "Résultats d'icônes")}
        style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(52px, 1fr))",
          gap: "6px", maxHeight: "260px", overflowY: "auto", padding: "4px",
        }}
      >
        {results.map((icon) => {
          const Icon = icon.Component;
          const isSelected = selected?.id === icon.id;
          return (
            <button
              key={icon.id}
              type="button"
              role="option"
              aria-selected={isSelected}
              title={icon.label}
              onClick={() => onSelect(icon.id)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                gap: "3px", padding: "8px 4px", borderRadius: "8px", cursor: "pointer",
                border: `1px solid ${isSelected ? "#6d28d9" : "#ece9f5"}`,
                background: isSelected ? "#f5f3ff" : "#fff",
                color: isSelected ? "#6d28d9" : "#4a4a68",
              }}
            >
              <Icon size={18} />
            </button>
          );
        })}
        {results.length === 0 && (
          <div style={{ gridColumn: "1 / -1", padding: "20px 10px", textAlign: "center", color: "#9b93b8", fontSize: "12px" }}>{elakayAutoText("h_b6ce306a6361f4", "Aucune icône ne correspond à «")}{query} ».
          </div>
        )}
      </div>
    </div>
  );
}

function categoryPillStyle(active) {
  return {
    padding: "4px 10px", fontSize: "11px", fontWeight: 600, borderRadius: "999px",
    cursor: "pointer", border: `1px solid ${active ? "#6d28d9" : "#e4dcfb"}`,
    background: active ? "#6d28d9" : "#f5f3ff", color: active ? "#fff" : "#6d28d9",
  };
}
